import { createGermanNumberFormatter } from '@utils/utils-format'
import { tv, type VariantProps } from 'tailwind-variants'

const variants = tv({
  base: '',
  variants: {
    strikethrough: {
      true: 'line-through opacity-80',
    },
  },
  defaultVariants: {
    strikethrough: false,
  },
})

export type PriceProps = VariantProps<typeof variants> & {
  amount?: number
  currency?: string
  className?: string | undefined
  maximumFractionDigits?: number
  dataTestId?: string
  microdata?: boolean
}

export function Price({
  amount = 0,
  currency = 'EUR',
  className,
  maximumFractionDigits,
  strikethrough,
  dataTestId,
  microdata,
}: PriceProps) {
  const numberFormat = createGermanNumberFormatter({
    style: 'currency',
    currency,
    maximumFractionDigits,
  })

  const value = amount / 100

  if (!microdata) {
    return (
      <span className={variants({ strikethrough, className })} data-testid={dataTestId}>
        {numberFormat.format(value)}
      </span>
    )
  }

  const parts = numberFormat.formatToParts(value)
  const symbol = parts.find(part => part.type === 'currency')?.value ?? ''
  const separator = parts.find(part => part.type === 'literal')?.value ?? ''
  const number = parts
    .filter(part => part.type !== 'currency' && part.type !== 'literal')
    .map(part => part.value)
    .join('')
  const isSymbolFirst =
    parts.findIndex(part => part.type === 'currency') <
    parts.findIndex(part => part.type === 'integer')

  const currencyElement = (
    <span itemProp="priceCurrency" content={currency}>
      {symbol}
    </span>
  )

  return (
    <span className={variants({ strikethrough, className })} data-testid={dataTestId}>
      {isSymbolFirst && (
        <>
          {currencyElement}
          {separator}
        </>
      )}
      <span itemProp="price">{number}</span>
      {!isSymbolFirst && (
        <>
          {separator}
          {currencyElement}
        </>
      )}
    </span>
  )
}
